import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useSubscription } from "@/subscription/SubscriptionContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useSubscription();
  const [state, setState] = useState("checking"); // checking | paid | timeout
  const sessionId = params.get("session_id");

  const poll = useCallback(
    async (attempt) => {
      if (!sessionId) {
        setState("timeout");
        return;
      }
      if (attempt >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          await refresh();
          return;
        }
      } catch (_) {
        /* keep polling */
      }
      setTimeout(() => poll(attempt + 1), 2000);
    },
    [sessionId, refresh]
  );

  useEffect(() => {
    poll(0);
  }, [poll]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center p-6 grain">
      <div className="max-w-md w-full text-center rounded-2xl border border-[#00e5ff]/40 bg-[#0d1416] p-10">
        {state === "checking" && (
          <>
            <Loader2 size={48} className="text-[#00e5ff] mx-auto animate-spin" />
            <h1 className="font-display font-black text-2xl uppercase mt-6" data-testid="payment-checking">Confirming your subscription…</h1>
            <p className="text-neutral-400 text-sm mt-2">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={48} className="text-[#00ff66] mx-auto" />
            <h1 className="font-display font-black text-3xl uppercase mt-6" data-testid="payment-success">You're Pro! 🎉</h1>
            <p className="text-neutral-300 text-sm mt-3">
              Your 7-day free trial is active. Unlimited AI, unlimited saves and WAV export are all unlocked.
            </p>
            <button
              data-testid="success-open-studio-btn"
              onClick={() => navigate("/studio")}
              className="mt-8 px-7 py-3.5 rounded-full bg-[#00e5ff] text-black font-bold text-sm hover:scale-105 transition-transform"
            >
              Back to the Studio
            </button>
          </>
        )}
        {state === "timeout" && (
          <>
            <Loader2 size={48} className="text-[#00e5ff] mx-auto" />
            <h1 className="font-display font-black text-2xl uppercase mt-6" data-testid="payment-timeout">Almost there…</h1>
            <p className="text-neutral-400 text-sm mt-3">
              Your payment is processing. Your Pro features will unlock automatically in a minute — head back to the studio.
            </p>
            <button
              onClick={() => navigate("/studio")}
              className="mt-8 px-7 py-3.5 rounded-full bg-[#00e5ff] text-black font-bold text-sm hover:scale-105 transition-transform"
            >
              Back to the Studio
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center p-6 grain">
      <div className="max-w-md w-full text-center rounded-2xl border border-[#262626] bg-[#141414] p-10">
        <XCircle size={48} className="text-neutral-500 mx-auto" />
        <h1 className="font-display font-black text-2xl uppercase mt-6" data-testid="payment-cancel">Checkout cancelled</h1>
        <p className="text-neutral-400 text-sm mt-3">No charge was made. You can upgrade any time from the studio.</p>
        <button
          onClick={() => navigate("/studio")}
          className="mt-8 px-7 py-3.5 rounded-full bg-[#00e5ff] text-black font-bold text-sm hover:scale-105 transition-transform"
        >
          Back to the Studio
        </button>
      </div>
    </div>
  );
}
